import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

import { ViewerContextSchema } from '../viewer-context/viewer-context';

/**
 * One member's decision about one of their own `user_controlled` fields, in one
 * context (`profile.answer_visibility_prefs`).
 *
 * Addressed by **question key**, not question id: the key is the stable slug
 * that survives every edit to a question's wording, and it is what the privacy
 * screen already has in hand from the composed form.
 */
export const AnswerVisibilityPrefSchema = z
  .object({
    questionId: UuidSchema,
    questionKey: z.string().min(1),
    context: ViewerContextSchema,
    visible: z.boolean(),
  })
  .strict();

export type AnswerVisibilityPref = z.infer<typeof AnswerVisibilityPrefSchema>;

/**
 * One toggle in a preferences write.
 *
 * `visible` is a strict boolean, never a coerced one (N7): a `'false'` string
 * arriving from a client and being read as `true` would expose a field the
 * member had just hidden.
 */
export const WriteAnswerVisibilityPrefSchema = z
  .object({
    questionKey: z.string().min(1).max(64),
    context: ViewerContextSchema,
    visible: z.boolean(),
  })
  .strict();

export type WriteAnswerVisibilityPref = z.infer<typeof WriteAnswerVisibilityPrefSchema>;

/**
 * The privacy screen submits its switches as a batch, so a member flipping
 * several fields is one request and one audit-visible act rather than a burst
 * that can half-fail.
 *
 * Bounded at 200 for the same reason every list is bounded: the number of
 * `user_controlled` questions is operator-defined, and an unbounded write is a
 * request whose cost a client chooses.
 */
export const WriteAnswerVisibilityPrefsSchema = z
  .object({
    preferences: z.array(WriteAnswerVisibilityPrefSchema).min(1).max(200),
  })
  .strict()
  // A batch naming the same field **and context** twice is ambiguous — which
  // switch wins is not something the server should decide silently. It is also
  // a hard database error rather than a judgement call: the whole batch is one
  // `INSERT … ON CONFLICT DO UPDATE`, and Postgres refuses outright with
  // "ON CONFLICT DO UPDATE command cannot affect row a second time" (SQLSTATE
  // 21000), which no filter maps — so without this the member's privacy screen
  // answers **500** and loses every toggle in the request.
  //
  // Checked here as well as in the service so mobile pre-validates it too (U3),
  // and stated as one rule rather than two implementations of it.
  .refine(
    (body) =>
      new Set(body.preferences.map((pref) => `${pref.questionKey}:${pref.context}`)).size ===
      body.preferences.length,
    {
      message: 'The same field and context appears more than once.',
      path: ['preferences'],
    },
  );

export type WriteAnswerVisibilityPrefsRequest = z.infer<typeof WriteAnswerVisibilityPrefsSchema>;
